import { IPermitAmendment } from "@mds/common/interfaces/permits";
import React, { FC, useMemo } from "react";

interface PermitConditionsContextType {
  mineGuid?: string;
  permitGuid?: string;
  latestAmendment?: IPermitAmendment;
  previousAmendment?: IPermitAmendment;
  currentAmendment?: IPermitAmendment;
  standardConditionType?: string;
  isStandardConditionEditor?: boolean;
  isNowEditor?: boolean;
  isCore?: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  refreshData: () => Promise<any>;
  activeConditionId: number | null;
  setActiveConditionId: (id: number | null) => void;
  clearActiveConditionId: (id: number) => void;
  submittingConditionIds: Record<number, number>;
  addSubmittingCondition: (id: number) => void;
  removeSubmittingCondition: (id: number) => void;
}

const PermitConditionsContext = React.createContext<PermitConditionsContextType | undefined>(
  undefined
);

export const usePermitConditions = () => {
  const context = React.useContext(PermitConditionsContext);
  if (!context) {
    throw new Error("usePermitConditions must be used within a PermitConditionsProvider");
  }
  return context;
};

export type PermitConditionsProviderInput =
  Partial<Omit<PermitConditionsContextType, "loading" | "setLoading" | "refreshData">> & {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    refreshData: () => Promise<any>;
  };

export const PermitConditionsProvider: FC<{
  children: React.ReactNode;
  value: PermitConditionsProviderInput;
}> = ({ children, value }) => {
  // undefined default value causes problems in permit condition form
  const defaultValue = {
    isNowEditor: false,
    isStandardConditionEditor: false,
    isCore: false,
    activeConditionId: null,
    setActiveConditionId: () => { },
    clearActiveConditionId: () => { },
    submittingConditionIds: {},
    addSubmittingCondition: () => { },
    removeSubmittingCondition: () => { },
  };

  const contextValue = useMemo(() => {
    return {
      ...defaultValue,
      ...value,
    };
  }, [value]);

  return (
    <PermitConditionsContext.Provider value={contextValue}>
      {children}
    </PermitConditionsContext.Provider>
  );
};
